import { useState, useCallback, useEffect, useRef } from "react";
import { useBluelagoon, Bluelagoon, useTaskWebSocket } from "@bluelagoon";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const GITHUB_APP_SLUG = "agentui-ai";

const isTerminal = (status) =>
    status === "COMPLETED" || status === "FAILED" || status === "CANCELLED";

/**
 * Pull the structured error shape (code/message/action) out of a failed
 * backgroundTask. The worker's silent-failure detector stores the handler's
 * return value under task.output.handlerResult, so the typed error lives at
 * task.output.handlerResult.error. Fall back to the plain string fields for
 * older/generic failure paths.
 */
function extractTaskError(task) {
    const structured = task?.output?.handlerResult?.error;
    if (structured && typeof structured === "object") {
        return structured; // { code, message, action }
    }
    const message =
        (typeof task?.error === "string" ? task.error : null) ||
        (typeof task?.output?.error === "string" ? task.output.error : null) ||
        undefined;
    return { message };
}

export function useGitHubSync(workflow, selectedEnvironment, refetchWorkflow, fetchComponents) {
    const { t } = useTranslation();

    const [pushTaskId, setPushTaskId] = useState(null);
    const [pullTaskId, setPullTaskId] = useState(null);
    const [isEnqueuingPush, setIsEnqueuingPush] = useState(false);
    const [isEnqueuingPull, setIsEnqueuingPull] = useState(false);

    const isPushing = isEnqueuingPush || !!pushTaskId;
    const isPulling = isEnqueuingPull || !!pullTaskId;

    const { call: gitHubPush } = useBluelagoon({ method: "gitHubPush" });
    const { call: gitHubPull } = useBluelagoon({ method: "gitHubPull" });
    const { call: getGitHubAuthUrl } = useBluelagoon({ method: "getGitHubAuthUrl" });

    const reconnect = useCallback(async () => {
        if (!workflow?.id) return;
        const result = await getGitHubAuthUrl({
            workflowId: workflow.id,
            currentUrl: window.location.href,
        });
        if (result?.data?.url) window.location.href = result.data.url;
    }, [workflow?.id, getGitHubAuthUrl]);

    const showError = useCallback((err, fallbackKey) => {
        const msg = err?.message || t(fallbackKey);
        const opts = {};
        if (err?.action === "reconnect") {
            opts.action = {
                label: t("appPreview.gitHubReconnect") || "Reconnect",
                onClick: () => reconnect(),
            };
        } else if (err?.action === "manage_repo_access") {
            opts.action = {
                label: t("appPreview.gitHubManageRepoAccess") || "Manage repo access",
                onClick: () => window.open(`https://github.com/apps/${GITHUB_APP_SLUG}/installations/new`, "_blank", "noopener,noreferrer"),
            };
        }
        toast.error(msg, opts);
    }, [t, reconnect]);

    // Stable refs for terminal handlers so the WebSocket callbacks and the
    // initial-fetch effects share the same body without re-subscribing.
    const handlePushTerminalRef = useRef();
    const handlePullTerminalRef = useRef();

    handlePushTerminalRef.current = (task) => {
        if (!task?.status || !isTerminal(task.status)) return false;
        if (task.status === "COMPLETED") {
            const msg = task.output?.message || t("appPreview.gitHubPushSuccess");
            toast.success(msg);
        } else {
            const err = extractTaskError(task);
            showError(
                { message: err.message || t("appPreview.gitHubPushError"), action: err.action, code: err.code },
                "appPreview.gitHubPushError"
            );
        }
        setPushTaskId(null);
        return true;
    };

    handlePullTerminalRef.current = (task) => {
        if (!task?.status || !isTerminal(task.status)) return false;
        if (task.status === "COMPLETED") {
            const msg = task.output?.message || t("appPreview.gitHubPullSuccess");
            toast.success(msg);
            refetchWorkflow?.();
            fetchComponents?.();
        } else {
            const err = extractTaskError(task);
            showError(
                { message: err.message || t("appPreview.gitHubPullError"), action: err.action, code: err.code },
                "appPreview.gitHubPullError"
            );
        }
        setPullTaskId(null);
        return true;
    };

    useTaskWebSocket({
        taskId: pushTaskId,
        enabled: !!pushTaskId,
        onUpdate: useCallback((data) => {
            const task = data?.data || data;
            handlePushTerminalRef.current(task);
        }, []),
    });

    useTaskWebSocket({
        taskId: pullTaskId,
        enabled: !!pullTaskId,
        onUpdate: useCallback((data) => {
            const task = data?.data || data;
            handlePullTerminalRef.current(task);
        }, []),
    });

    // Initial status fetch whenever we start subscribing to a task.
    //
    // Fixes the race where a task finishes before the WebSocket is attached
    // (small pushes complete in <1s — faster than WS handshake). Also handles
    // the 409 PUSH_IN_PROGRESS reattach path, where the existing task may
    // already be terminal. Without this, pushTaskId/pullTaskId would never
    // clear and the button stays stuck.
    useEffect(() => {
        if (!pushTaskId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await Bluelagoon.getTaskStatus({ taskId: pushTaskId });
                if (cancelled) return;
                const task = res?.data;
                if (task && isTerminal(task.status)) {
                    handlePushTerminalRef.current(task);
                }
            } catch {
                // Non-fatal: the WS/polling fallback will catch the terminal
                // event once it connects. Silencing because a transient 5xx
                // here shouldn't clear the button state.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [pushTaskId]);

    useEffect(() => {
        if (!pullTaskId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await Bluelagoon.getTaskStatus({ taskId: pullTaskId });
                if (cancelled) return;
                const task = res?.data;
                if (task && isTerminal(task.status)) {
                    handlePullTerminalRef.current(task);
                }
            } catch {
                // See push effect.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [pullTaskId]);

    const handlePush = useCallback(async () => {
        if (!workflow?.id || isPushing) return;
        setIsEnqueuingPush(true);
        try {
            const enqueue = await gitHubPush({ workflowId: workflow.id, environment: selectedEnvironment });

            // Async path (default): 202 with taskId → hand off to useTaskWebSocket
            // + the initial-fetch effect (for fast-finish / reattach races).
            if (enqueue?.data?.taskId && enqueue?.data?.status === "PENDING") {
                setPushTaskId(enqueue.data.taskId);
                return;
            }

            // Sync path fallback (background:false forced somewhere upstream).
            if (enqueue?.success) {
                toast.success(enqueue.message || t("appPreview.gitHubPushSuccess"));
            } else {
                showError(enqueue?.error, "appPreview.gitHubPushError");
            }
        } catch (error) {
            // 409 PUSH_IN_PROGRESS surfaces the existing taskId — subscribe
            // to it so the user sees the in-flight push complete.
            const existingTaskId = error?.data?.taskId;
            if (error?.status === 409 && existingTaskId) {
                setPushTaskId(existingTaskId);
                return;
            }
            showError(error?.error, "appPreview.gitHubPushError");
        } finally {
            setIsEnqueuingPush(false);
        }
    }, [workflow?.id, isPushing, gitHubPush, selectedEnvironment, t, showError]);

    const handlePull = useCallback(async () => {
        if (!workflow?.id || isPulling) return;
        setIsEnqueuingPull(true);
        try {
            const enqueue = await gitHubPull({ workflowId: workflow.id, environment: selectedEnvironment });

            if (enqueue?.data?.taskId && enqueue?.data?.status === "PENDING") {
                setPullTaskId(enqueue.data.taskId);
                return;
            }

            if (enqueue?.success) {
                toast.success(enqueue.message || t("appPreview.gitHubPullSuccess"));
                refetchWorkflow?.();
                fetchComponents?.();
            } else {
                showError(enqueue?.error, "appPreview.gitHubPullError");
            }
        } catch (error) {
            const existingTaskId = error?.data?.taskId;
            if (error?.status === 409 && existingTaskId) {
                setPullTaskId(existingTaskId);
                return;
            }
            showError(error?.error, "appPreview.gitHubPullError");
        } finally {
            setIsEnqueuingPull(false);
        }
    }, [workflow?.id, isPulling, gitHubPull, refetchWorkflow, fetchComponents, selectedEnvironment, t, showError]);

    return { isPushing, isPulling, handlePush, handlePull };
}
